import { useCallback, useState } from 'react';
import PageContainer from '../components/PageContainer';
import TimeRangeSelector, { ranges } from '../components/TimeRangeSelector';
import LogDetailsTable from '../components/LogDetailsTable';
import VisitorLogsTable from '../components/VisitorLogsTable';
import GeofencesTable from '../components/GeofencesTable';
import { formatDateForApi } from '../utils/date';
import useAutoRefresh from '../hooks/useAutoRefresh';

const panelClasses =
  'flex flex-col min-h-0 overflow-hidden rounded-3xl border border-white/10 bg-white/70 p-5 text-slate-900 shadow-xl shadow-slate-900/10 backdrop-blur-xl dark:border-white/5 dark:bg-slate-900/60 dark:text-white dark:shadow-black/20';

export default function LogDetails() {
  const [range, setRange] = useState(ranges[1]);
  const [counts, setCounts] = useState({ visitors: 0, dashboard: 0, allTime: 0 });
  const [equipmentData, setEquipmentData] = useState([]);
  const [geofenceData, setGeofenceData] = useState([]);
  const [visitorData, setVisitorData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('none');

  const fetchData = useCallback(async () => {
    const end = new Date();
    const start = new Date(end);
    if (range.unit === 'hour') {
      start.setHours(end.getHours() - range.value);
    } else {
      start.setDate(end.getDate() - range.value);
    }
    try {
      const [cRes, dRes, gRes] = await Promise.all([
        fetch(
          `/api/user_counts.php?start=${formatDateForApi(start)}&end=${formatDateForApi(end)}`
        ),
        fetch(
          `/api/equipment_status.php?start=${formatDateForApi(start)}&end=${formatDateForApi(end)}`
        ),
        fetch(
          `/api/geofences.php?start=${formatDateForApi(start)}&end=${formatDateForApi(end)}`
        ),
      ]);
      const cJson = await cRes.json();
      const dJson = await dRes.json();
      const gJson = await gRes.json();
      setCounts({
        visitors: cJson.visitors || 0,
        dashboard: cJson.dashboard || 0,
        allTime: cJson.allTime || 0,
      });
      setEquipmentData(dJson);
      setGeofenceData(gJson);
    } catch (e) {
      console.error(e);
    }
  }, [range]);

  useAutoRefresh(fetchData, [range]);

  const fetchVisitorData = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('sort_by', sortBy || 'created_at');
    params.set('sort_dir', sortDir);
    try {
      const res = await fetch(`/api/visitor_logs.php?${params.toString()}`);
      const json = await res.json();
      setVisitorData(json);
    } catch (e) {
      console.error(e);
    }
  }, [sortBy, sortDir]);

  useAutoRefresh(fetchVisitorData, [sortBy, sortDir]);

  const handleSort = (column) => {
    if (sortBy === column && sortDir === 'desc') {
      setSortDir('asc');
    } else if (sortBy === column && sortDir === 'asc') {
      setSortBy(null);
      setSortDir('none');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const summaries = [
    {
      label: 'Visitors',
      value: counts.visitors,
      description: 'Unique entries for the selected window',
      gradient: 'from-sky-400/90 to-blue-500/70',
    },
    {
      label: 'Dashboard Visitors',
      value: counts.dashboard,
      description: 'Users monitoring live dashboards',
      gradient: 'from-emerald-400/90 to-teal-500/70',
    },
    {
      label: 'All Time Visitors',
      value: counts.allTime,
      description: 'Historical total interactions',
      gradient: 'from-amber-400/90 to-orange-500/70',
    },
  ];

  return (
    <PageContainer className="relative overflow-hidden bg-slate-950 text-slate-100">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.16),transparent_55%)]" />
      <div className="relative z-10 flex flex-1 flex-col gap-8">
        <header className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-200">
                Audit Trail Center
              </span>
              <div>
                <h1 className="text-3xl font-semibold leading-tight text-white md:text-4xl">Log Details</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-300">
                  Complete visibility into visitor journeys, geofence activity, and equipment history for compliance-ready reporting.
                </p>
              </div>
            </div>
            <TimeRangeSelector value={range} onChange={setRange} />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {summaries.map(({ label, value, description, gradient }) => (
              <div
                key={label}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/10 p-5 transition hover:border-white/30 hover:bg-white/20 dark:border-white/5 dark:bg-white/5"
              >
                <div className={`absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br opacity-80 blur-2xl transition group-hover:opacity-100 ${gradient}`} />
                <div className="relative z-10 flex flex-col gap-2 text-left">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-200">{label}</span>
                  <span className="text-3xl font-semibold text-white">{value}</span>
                  <span className="text-xs text-slate-300">{description}</span>
                </div>
              </div>
            ))}
          </div>
        </header>

        <div className="grid flex-1 min-h-0 grid-cols-1 gap-6 lg:grid-cols-2">
          <section className={`${panelClasses}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Visitor Logs</h2>
              <span className="text-xs font-medium text-slate-300">Sortable history</span>
            </div>
            <div className="mt-4 flex-1 min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
              <VisitorLogsTable
                data={visitorData}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
            </div>
          </section>
          <section className={`${panelClasses}`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Geofences</h2>
              <span className="text-xs font-medium text-slate-300">Live boundaries</span>
            </div>
            <div className="mt-4 flex-1 min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
              <GeofencesTable data={geofenceData} />
            </div>
          </section>
          <section className={`${panelClasses} lg:col-span-2`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Equipment Logs</h2>
              <span className="text-xs font-medium text-slate-300">Chronological trace</span>
            </div>
            <div className="mt-4 flex-1 min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
              <LogDetailsTable data={equipmentData} />
            </div>
          </section>
        </div>
      </div>
    </PageContainer>
  );
}

